import { useEffect, useState, useCallback } from 'react';

/**
 * usePWAInstall — hook that abstracts the browser PWA install prompt.
 *
 * Returns:
 *   canInstall: true if the browser fired `beforeinstallprompt` and the
 *               app is not already installed.
 *   isIOS:      true if running on iOS Safari (no beforeinstallprompt →
 *               we must show manual instructions).
 *   isInstalled: true if launched in standalone / display-mode: standalone.
 *   install():  triggers the native install prompt. Returns 'accepted' /
 *               'dismissed' / 'unsupported'.
 */
export default function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const isIOS = typeof window !== 'undefined' &&
    /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
    !window.MSStream;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Detect standalone / already installed
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(standalone);

    const onBeforeInstall = (e) => {
      // Chrome / Edge / Samsung Internet fire this; prevent auto-prompt.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return 'unsupported';
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice.outcome; // 'accepted' | 'dismissed'
  }, [deferredPrompt]);

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isIOS,
    isInstalled,
    install,
  };
}
